// src/components/MobileMenuPortal.tsx
'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Logo from './Logo';
import Link from 'next/link';
import { FaTimes } from 'react-icons/fa';

type MobileMenuPortalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

export default function MobileMenuPortal({ isOpen, onClose, children }: MobileMenuPortalProps) {
  // Animasyon durumlarını takip etmek için state'ler
  const [isAnimating, setIsAnimating] = useState(false);
  const [showContent, setShowContent] = useState(false); // Yeni eklenen state
  
  // Menü açıkken scrollu engelle
  useEffect(() => {
    if (!isOpen && !isAnimating) return;
    
    // Mevcut scroll pozisyonunu kaydet
    const scrollPos = window.scrollY;
    
    // Scrollu engelleyen stilleri ekle
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPos}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Menü kapandığında stilleri kaldır ve scroll pozisyonunu geri yükle
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollPos);
    };
  }, [isOpen, isAnimating]);
  
  // isOpen değiştiğinde animasyon durumunu güncelle
  useEffect(() => {
    if (isOpen) {
      // Önce animasyonu başlat
      setIsAnimating(true);
      
      // 10ms sonra içeriği göster (bu gecikme, DOM'un güncellenip CSS geçişlerinin doğru çalışması için gereklidir)
      const showTimer = setTimeout(() => {
        setShowContent(true);
      }, 10);
      
      return () => clearTimeout(showTimer);
    } else {
      // Menü kapanırken, önce içeriği gizle
      setShowContent(false);
      
      // Sonra animasyon tamamlandığında bileşeni gizle
      const hideTimer = setTimeout(() => {
        setIsAnimating(false);
      }, 300); // 300ms, CSS transition süresiyle aynı
      
      return () => clearTimeout(hideTimer);
    }
  }, [isOpen]);
  
  // Menü kapalıysa ve animasyon yoksa hiçbir şey gösterme
  if (!isOpen && !isAnimating) return null;
  
  return createPortal(
    <div className="mobile-menu-container fixed inset-0 z-40"> 
      {/* Tam ekran kaplar ve navbar'ı da içerir */}
      
      {/* Karartma katmanı - Yumuşak opacity geçişi */}
      <div 
        className={`absolute inset-0 bg-black backdrop-blur-md transition-all duration-300 ease-in-out ${
          showContent ? 'bg-opacity-80 opacity-100' : 'bg-opacity-0 opacity-0'
        }`}
        onClick={onClose}
      />
      
      {/* Özel navbar ekliyoruz - orijinal navbar'ın üzerine */}
      <div className="absolute top-0 left-0 w-full py-4 px-4 sm:px-6 lg:px-8 flex items-center justify-between z-10">
        <div className="flex-shrink-0">
          <Link href="/" aria-label="FynWise" onClick={onClose}>
            <Logo />
          </Link>
        </div>
        <button 
          onClick={onClose} 
          className="text-gray-400 hover:text-white focus:outline-none"
        >
          <FaTimes size={24} />
        </button>
      </div>
      
      {/* Menü paneli - Yumuşak sağdan sola kaydırma animasyonu */}
      <div 
        className={`absolute top-0 right-0 w-4/5 max-w-sm h-full bg-black overflow-y-auto transition-transform duration-300 ease-in-out transform ${
          showContent ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          boxShadow: "-5px 0 25px rgba(0, 0, 0, 0.3)",
          willChange: "transform", // Performans iyileştirmesi
          marginTop: "76px", // Navbar yüksekliğine eşit - bunu kendi navbar yüksekliğinize göre ayarlayın
          height: "calc(100% - 76px)" // Tam ekran yükseklik - navbar yüksekliği
        }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}